import { useEffect } from "react";
import Navbar from "@/components/site/Navbar";
import Footer from "@/components/site/Footer";
import { Reveal } from "@/components/site/motion";
import { CONTACT } from "@/data/content";
import useSEO from "@/hooks/useSEO";

const SECTIONS = [
  {
    title: "1. Who we are",
    body: [
      "New Beginnings Recovery Home (“New Beginnings”, “we”, “us”, “our”) operates a licensed inpatient recovery residence for drug & alcohol addiction and mental-health care, located at 69 Gradwell Drive, Scarborough, Ontario. This Privacy Policy explains how we collect, use, disclose and protect personal information — including personal health information — when you visit newbeginningsrecovery.ca or contact us.",
    ],
  },
  {
    title: "2. The laws we follow",
    body: [
      "As an Ontario health-care provider, we handle personal health information in accordance with Ontario’s Personal Health Information Protection Act, 2004 (PHIPA) and, where applicable, Canada’s Personal Information Protection and Electronic Documents Act (PIPEDA). PHIPA gives you specific rights over your personal health information, described in section 8 below.",
    ],
  },
  {
    title: "3. What we collect",
    body: [
      "When you submit a contact, insurance-verification or admissions enquiry form, we collect the information you provide: your name, email address, phone number, date of birth, insurance provider, and the contents of your message.",
      "When you browse the site, we may automatically collect limited technical information such as your IP address, browser type, pages visited and approximate location, used only to operate, secure and improve the website.",
    ],
  },
  {
    title: "4. How we use your information",
    body: [
      "We use the information you give us to respond to your enquiry, verify insurance benefits at your request, assess suitability for admission, coordinate your care, and meet legal, regulatory and accreditation requirements (including LegitScript and CARF processes).",
      "We do not use your health information for marketing, and we do not sell, rent or trade your personal information to anyone.",
    ],
  },
  {
    title: "5. Consent",
    body: [
      "By submitting a form on this website, you consent to our collection and use of the information provided for the purposes described above. You may withdraw your consent at any time, subject to legal and contractual restrictions, by contacting our Privacy Officer (section 10). Withdrawing consent may limit our ability to respond to your enquiry or provide services.",
    ],
  },
  {
    title: "6. How we share information",
    body: [
      "We share personal information only with your consent or as permitted or required by law. This may include sharing with your insurance provider (for benefit verification you request), members of your care team, laboratories or referral partners involved in your treatment, and regulators, courts or public-health authorities where the law requires it.",
      "Service providers that host or operate our website and systems may process information on our behalf under contractual confidentiality and security obligations.",
    ],
  },
  {
    title: "7. How we protect your information",
    body: [
      "We use administrative, technical and physical safeguards appropriate to the sensitivity of the information, including encrypted transmission (HTTPS), access controls limited to staff who need the information to do their jobs, staff confidentiality training, and secure retention and destruction practices.",
      "No method of transmission over the internet is 100% secure. If you prefer not to share sensitive details online, please call us instead.",
    ],
  },
  {
    title: "8. Your rights under PHIPA",
    body: [
      "You have the right to be informed of the purposes for which your personal health information is collected, used and disclosed; to access your personal health information; to request correction of incomplete or inaccurate records; to withdraw or vary your consent; and to be notified if your information is stolen, lost or accessed by unauthorized persons.",
      "If you believe your privacy rights have not been respected, you may complain to our Privacy Officer, and you have the right to complain to the Information and Privacy Commissioner of Ontario (www.ipc.on.ca, 2 Bloor Street East, Suite 1400, Toronto, ON M4W 1A8, 1-800-387-0073).",
    ],
  },
  {
    title: "9. Cookies and analytics",
    body: [
      "This site may use cookies and similar technologies for essential site functionality and to understand aggregate usage. You can control or delete cookies through your browser settings; disabling them may affect some site features.",
    ],
  },
  {
    title: "10. Retention and contact",
    body: [
      "We keep personal information only as long as necessary for the purposes described in this policy and as required by Ontario health-record retention rules, after which it is securely destroyed.",
      "Questions, access or correction requests, and privacy complaints may be directed to our Privacy Officer:",
      `New Beginnings Recovery Home — Privacy Officer, 69 Gradwell Drive, Scarborough, ON M1M 2N1 · ${CONTACT.email} · ${CONTACT.phone}`,
    ],
  },
  {
    title: "11. Changes to this policy",
    body: [
      "We may update this Privacy Policy from time to time. The “last updated” date below reflects the most recent revision. Material changes will be posted on this page.",
    ],
  },
];

const PrivacyPage = () => {
  useSEO({
    title: "Privacy Policy (PHIPA) | New Beginnings Recovery Toronto",
    description: "How New Beginnings Recovery Home in Scarborough, Toronto collects, uses and protects your personal health information under Ontario's PHIPA.",
    keywords: "privacy policy, PHIPA, personal health information, addiction treatment privacy Ontario",
    canonical: "https://newbeginningsrecovery.ca/privacy",
  });
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div data-testid="privacy-page" className="bg-sand min-h-screen">
      <Navbar />
      <section className="pt-40 md:pt-48 pb-12">
        <div className="mx-auto max-w-[900px] px-5 md:px-10">
          <Reveal>
            <p className="text-sm uppercase tracking-[0.25em] text-terracotta mb-4">Legal</p>
            <h1 className="font-serif text-5xl md:text-6xl text-forest tracking-tight leading-[0.98]">
              Privacy Policy
            </h1>
            <p className="mt-6 text-lg text-ink2 leading-relaxed">
              Your privacy — and the confidentiality of your health information — is fundamental to
              the trust you place in us. This policy explains, in plain language, how we protect it.
            </p>
            <p className="mt-4 text-sm text-ink2/70">Last updated: July 2026</p>
          </Reveal>
        </div>
      </section>

      <section className="pb-24 md:pb-32">
        <div className="mx-auto max-w-[900px] px-5 md:px-10 space-y-10">
          {SECTIONS.map((s) => (
            <Reveal key={s.title}>
              <div>
                <h2 className="font-serif text-2xl md:text-3xl text-forest tracking-tight">{s.title}</h2>
                {s.body.map((p, i) => (
                  <p key={i} className="mt-3 text-ink2 leading-relaxed">{p}</p>
                ))}
              </div>
            </Reveal>
          ))}
          <Reveal>
            <p className="text-sm text-ink2/70 border-t border-line pt-6">
              This policy is provided for general informational purposes and does not constitute legal advice.
            </p>
          </Reveal>
        </div>
      </section>
      <Footer />
    </div>
  );
};

export default PrivacyPage;
